import axios, { AxiosError } from 'axios';
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { selectFilters } from '../../Filter/selectors';
import type Document from '../../Document';
import generateCorrelationId from '../../../utils';
import { FetchDocumentsResult } from '../../Document/hooks/types';
import { useEffect, useMemo } from 'react';
import { buildDocumentsQueryKey, useMarkDocumentAsRead } from '../../Document';

export async function fetchDocumentMetadataById(
  id: string,
  sourceType?: string
): Promise<Document> {
  const baseUrl = '/customer/document/archive';
  const source = sourceType || 'MAIN';
  const isBank = source.toUpperCase() === 'BANK';
  const endpoint = isBank
    ? `${baseUrl}/bank/documents/${id}/metadata`
    : `${baseUrl}/documents/${id}/metadata`;
  const { data } = await axios.get<Document.MetadataAPI>(endpoint, {
    headers: {
      'X-Correlation-ID': `document-archive-web.${generateCorrelationId()}`,
    },
  });

  const { payload } = data;

  return {
    id: payload.id,
    name: payload.documentName,
    date: payload.filedDate || payload.fileCreationDate,
    contractNumbers: payload.contractNumbers ?? [],
    products: Array.isArray(payload.productDetails)
      ? payload.productDetails?.map(({ fmsKey }) => fmsKey) || []
      : [],
    isRead: true,
    sourceType: payload.sourceType,
    documentIdentityCode: payload.documentIdentityCode,
  };
}

export function useDocument(
  id: Document.Id | undefined,
  sourceType?: Document.Source
) {
  const queryClient = useQueryClient();
  const activeFilters = useSelector(selectFilters);
  const source = sourceType || 'MAIN';
  const documentsQueryKey = useMemo(
    () => buildDocumentsQueryKey(activeFilters, activeFilters.segment),
    [activeFilters]
  );
  const markAsRead = useMarkDocumentAsRead(activeFilters.segment);
  const queryResult = useQuery<Document, AxiosError | null>({
    queryKey: ['documents', 'metadata', id, source],
    queryFn: () => {
      if (!id) {
        throw new Error('Document ID is required');
      }
      return fetchDocumentMetadataById(id, source);
    },
    placeholderData: () => {
      const cachedData =
        queryClient.getQueryData<FetchDocumentsResult>(documentsQueryKey);

      const cachedDocument = cachedData?.documents.find(
        (document) => document.id === id
      );

      if (cachedDocument) {
        return cachedDocument;
      }

      return undefined;
    },
    gcTime: Infinity,
    staleTime: Infinity,
    enabled: id !== undefined,
    meta: {
      datadogErrorMessage: `Failed to fetch document metadata by id ${id}`,
    },
    retry: (failureCount, error) => {
      return failureCount < 2 && error?.status != 404;
    },
  });

  const { data } = queryResult;

  useEffect(() => {
    if (!data?.isRead) {
      return;
    }
    markAsRead(data.id);
  }, [data, markAsRead]);

  return queryResult;
}
